import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  FileText, Eye, Check, X, DollarSign, FileSignature,
  RefreshCw, Building, User, Calendar, CreditCard,
  Download, Mail, FileDown, Loader2
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  pending_payment: 'bg-yellow-100 text-yellow-800',
  payment_uploaded: 'bg-blue-100 text-blue-800',
  payment_verified: 'bg-green-100 text-green-800',
  loan_application: 'bg-purple-100 text-purple-800',
  loan_approved: 'bg-indigo-100 text-indigo-800',
  loan_rejected: 'bg-red-100 text-red-800',
  spa_pending: 'bg-orange-100 text-orange-800',
  spa_signed: 'bg-emerald-100 text-emerald-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_payment: 'Pending Payment',
  payment_uploaded: 'Payment Uploaded',
  payment_verified: 'Payment Verified',
  loan_application: 'Loan Application',
  loan_approved: 'Loan Approved',
  loan_rejected: 'Loan Rejected',
  spa_pending: 'SPA Pending',
  spa_signed: 'SPA Signed',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [pdfLoading, setPdfLoading] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');

  useEffect(() => {
    loadBookings();
  }, [statusFilter]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await bookingAPI.getBookings(params);
      setBookings(res.data || []);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPayment = async (verified) => {
    try {
      await bookingAPI.verifyPayment(selectedBooking.id, verified, actionNotes);
      toast.success(verified ? 'Payment verified' : 'Payment rejected');
      setShowActionModal(false);
      setActionNotes('');
      loadBookings();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const handleUpdateLoan = async (status) => {
    try {
      await bookingAPI.updateLoanStatus(
        selectedBooking.id,
        status,
        selectedBooking.loan_bank || '',
        selectedBooking.loan_amount || 0,
        actionNotes
      );
      toast.success(`Loan ${status}`);
      setShowActionModal(false);
      setActionNotes('');
      loadBookings();
    } catch (error) {
      toast.error('Failed to update loan status');
    }
  };

  const handleUpdateSPA = async (signed) => {
    try {
      await bookingAPI.updateSPA(selectedBooking.id, signed, '', actionNotes);
      toast.success(signed ? 'SPA marked as signed' : 'SPA pending');
      setShowActionModal(false);
      setActionNotes('');
      loadBookings();
    } catch (error) {
      toast.error('Failed to update SPA status');
    }
  };

  const handleDownloadPdf = async (booking) => {
    setPdfLoading(booking.id);
    try {
      const response = await bookingAPI.downloadPdf(booking.id);
      const { pdf_base64, filename } = response.data;
      
      // Convert base64 to blob
      const byteCharacters = atob(pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `booking_${booking.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setPdfLoading(null);
    }
  };

  const handleOpenEmailModal = (booking) => {
    setSelectedBooking(booking);
    // Use customer email if available, otherwise try form_data.buyer_email
    const email = booking.customer?.email || booking.form_data?.buyer_email || '';
    setEmailAddress(email);
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailAddress) {
      toast.error('Please enter an email address');
      return;
    }
    setPdfLoading(selectedBooking.id);
    try {
      await bookingAPI.sendPdfEmail(selectedBooking.id, emailAddress, false, false);
      toast.success(`PDF sent to ${emailAddress}`);
      setShowEmailModal(false);
      setEmailAddress('');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setPdfLoading(null);
    }
  };

  const openActionModal = (booking, action) => {
    setSelectedBooking(booking);
    setActionType(action);
    setShowActionModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-bookings-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bookings Management</h1>
          <p className="text-gray-500">Manage property bookings and track status</p>
        </div>
        <Button variant="outline" onClick={loadBookings}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{bookings.length}</div>
            <p className="text-sm text-gray-500">Total Bookings</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-600">
              {bookings.filter(b => b.status === 'payment_uploaded').length}
            </div>
            <p className="text-sm text-gray-500">Pending Verification</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-purple-600">
              {bookings.filter(b => b.status === 'loan_application').length}
            </div>
            <p className="text-sm text-gray-500">Loan Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">
              {bookings.filter(b => b.status === 'spa_signed' || b.status === 'completed').length}
            </div>
            <p className="text-sm text-gray-500">Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Bookings Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Property / Unit</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No bookings found
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking) => (
                  <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="font-medium">{booking.property?.name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">{booking.unit?.unit_number || 'N/A'}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="font-medium">{booking.customer?.full_name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500">{booking.customer?.phone || ''}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p>{booking.agent?.full_name || 'Unknown'}</p>
                      <p className="text-sm text-gray-500">{booking.agent?.agent_code || ''}</p>
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatCurrency(booking.unit?.price)}
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[booking.status] || 'bg-gray-100'}>
                        {STATUS_LABELS[booking.status] || booking.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {formatDate(booking.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedBooking(booking);
                            setShowDetailModal(true);
                          }}
                          data-testid={`view-booking-${booking.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        
                        {/* PDF Download - available for submitted/completed bookings */}
                        {['submitted', 'payment_verified', 'loan_approved', 'spa_signed', 'completed'].includes(booking.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600"
                            onClick={() => handleDownloadPdf(booking)}
                            disabled={pdfLoading === booking.id}
                            title="Download PDF"
                            data-testid={`download-pdf-${booking.id}`}
                          >
                            {pdfLoading === booking.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        
                        {/* Email PDF - available for completed bookings */}
                        {['spa_signed', 'completed'].includes(booking.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-emerald-600"
                            onClick={() => handleOpenEmailModal(booking)}
                            title="Email PDF"
                            data-testid={`email-pdf-${booking.id}`}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {booking.status === 'payment_uploaded' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-green-600"
                            onClick={() => openActionModal(booking, 'verify_payment')}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {booking.status === 'loan_application' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-purple-600"
                            onClick={() => openActionModal(booking, 'loan')}
                          >
                            <CreditCard className="h-4 w-4" />
                          </Button>
                        )}
                        
                        {(booking.status === 'loan_approved' || booking.status === 'payment_verified') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-indigo-600"
                            onClick={() => openActionModal(booking, 'spa')}
                          >
                            <FileSignature className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-detail-modal">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                <Badge className={`${STATUS_COLORS[selectedBooking.status]} text-sm px-3 py-1`}>
                  {STATUS_LABELS[selectedBooking.status]}
                </Badge>
                <span className="text-sm text-gray-500">
                  Created: {formatDate(selectedBooking.created_at)}
                </span>
              </div>

              {/* Property & Unit */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Property</p>
                  <p className="font-medium">{selectedBooking.property?.name}</p>
                  <p className="text-sm text-gray-500">{selectedBooking.property?.location}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Unit</p>
                  <p className="font-medium">{selectedBooking.unit?.unit_number}</p>
                  <p className="text-sm text-gray-500">
                    {selectedBooking.unit?.unit_type} • {formatCurrency(selectedBooking.unit?.price)}
                  </p>
                </div>
              </div>

              {/* Customer & Agent */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Customer</p>
                  <p className="font-medium">{selectedBooking.customer?.full_name}</p>
                  <p className="text-sm text-gray-600">{selectedBooking.customer?.email}</p>
                  <p className="text-sm text-gray-600">{selectedBooking.customer?.phone}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600 mb-1">Agent</p>
                  <p className="font-medium">{selectedBooking.agent?.full_name}</p>
                  <p className="text-sm text-gray-600">{selectedBooking.agent?.agent_code}</p>
                  <p className="text-sm text-gray-600">{selectedBooking.agent?.phone}</p>
                </div>
              </div>

              {/* Form Data */}
              {selectedBooking.form_data && Object.keys(selectedBooking.form_data).length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Form Data</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(selectedBooking.form_data).map(([key, value]) => (
                      <div key={key} className="flex justify-between p-2 bg-gray-50 rounded">
                        <span className="text-gray-500">{key.replace(/_/g, ' ')}</span>
                        <span className="font-medium">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Loan Info */}
              {selectedBooking.loan_bank && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600 mb-1">Loan Details</p>
                  <p className="font-medium">Bank: {selectedBooking.loan_bank}</p>
                  <p className="text-sm">Amount: {formatCurrency(selectedBooking.loan_amount)}</p>
                  <p className="text-sm">Status: {selectedBooking.loan_status}</p>
                </div>
              )}

              {/* Payment Slip */}
              {selectedBooking.payment_slip && (
                <div>
                  <h4 className="font-medium mb-2">Payment Slip</h4>
                  <img 
                    src={selectedBooking.payment_slip} 
                    alt="Payment Slip" 
                    className="max-w-full h-auto border rounded-lg"
                  />
                </div>
              )}

              {/* Status History */}
              {selectedBooking.status_history?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Status History</h4>
                  <div className="space-y-2">
                    {selectedBooking.status_history.slice().reverse().map((entry, index) => (
                      <div key={index} className="flex items-start gap-3 text-sm">
                        <span className="text-gray-400">{formatDate(entry.changed_at)}</span>
                        <Badge variant="outline">{STATUS_LABELS[entry.status] || entry.status}</Badge>
                        {entry.notes && <span className="text-gray-500">{entry.notes}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PDF Actions for completed bookings */}
              {['submitted', 'payment_verified', 'loan_approved', 'spa_signed', 'completed'].includes(selectedBooking.status) && (
                <div className="flex gap-3 pt-4 border-t">
                  <Button 
                    onClick={() => handleDownloadPdf(selectedBooking)}
                    disabled={pdfLoading === selectedBooking.id}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="modal-download-pdf"
                  >
                    {pdfLoading === selectedBooking.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Download PDF
                  </Button>
                  {['spa_signed', 'completed'].includes(selectedBooking.status) && (
                    <Button 
                      onClick={() => {
                        setShowDetailModal(false);
                        handleOpenEmailModal(selectedBooking);
                      }}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="modal-email-pdf"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Email PDF
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent data-testid="action-modal">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'verify_payment' && 'Verify Payment'}
              {actionType === 'loan' && 'Update Loan Status'}
              {actionType === 'spa' && 'Update SPA Status'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes (optional)</label>
              <Input
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                placeholder="Add notes..."
                data-testid="action-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActionModal(false)}>
              Cancel
            </Button>
            
            {actionType === 'verify_payment' && (
              <>
                <Button variant="destructive" onClick={() => handleVerifyPayment(false)}>
                  <X className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button onClick={() => handleVerifyPayment(true)}>
                  <Check className="h-4 w-4 mr-2" />
                  Verify
                </Button>
              </>
            )}
            
            {actionType === 'loan' && (
              <>
                <Button variant="destructive" onClick={() => handleUpdateLoan('rejected')}>
                  Reject Loan
                </Button>
                <Button onClick={() => handleUpdateLoan('approved')}>
                  Approve Loan
                </Button>
              </>
            )}
            
            {actionType === 'spa' && (
              <Button onClick={() => handleUpdateSPA(true)}>
                <FileSignature className="h-4 w-4 mr-2" />
                Mark SPA Signed
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email PDF Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent data-testid="email-modal">
          <DialogHeader>
            <DialogTitle>Send Booking PDF via Email</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedBooking && (
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p><span className="text-gray-500">Booking:</span> <span className="font-medium">{selectedBooking.property?.name}</span></p>
                <p><span className="text-gray-500">Unit:</span> <span className="font-medium">{selectedBooking.unit?.unit_number}</span></p>
                <p><span className="text-gray-500">Customer:</span> <span className="font-medium">{selectedBooking.customer?.full_name}</span></p>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Recipient Email</label>
              <Input
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="Enter email address..."
                data-testid="email-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={pdfLoading === selectedBooking?.id}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {pdfLoading === selectedBooking?.id ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBookings;
